import type { Board, LevelConfig, Tile } from './types.js';
import { RULES, isMatch, newDealCtx, satisfies, valueRange, type DealCtx, type Face, type PairRule } from './rules.js';
import { Rng } from './rng.js';

/* ─── connectivity ──────────────────────────────────────────────────────────
 * Active only when `cfg.adjacency` is on (Grade 3+). Two uncleared tiles may
 * pair when ANY of three things is true. The second and third are what give
 * the video's game its strategy: cleared tiles stop blocking, so the board
 * keeps opening up as you play.
 * ─────────────────────────────────────────────────────────────────────────── */

/** 1. Immediate neighbours, including diagonals. */
export function gridAdjacent(cols: number, i: number, j: number): boolean {
  const dr = Math.abs(Math.floor(i / cols) - Math.floor(j / cols));
  const dc = Math.abs((i % cols) - (j % cols));
  return dr <= 1 && dc <= 1 && i !== j;
}

/** 2. Consecutive in reading order once cleared tiles are skipped — this is
 *     also what wraps the end of one row to the start of the next. */
export function readingAdjacent(tiles: readonly Tile[], i: number, j: number): boolean {
  const [lo, hi] = i < j ? [i, j] : [j, i];
  for (let k = lo + 1; k < hi; k++) if (!tiles[k]!.cleared) return false;
  return lo !== hi;
}

/** 3. Consecutive down a column once cleared tiles are skipped. */
export function columnAdjacent(tiles: readonly Tile[], cols: number, i: number, j: number): boolean {
  if (i % cols !== j % cols || i === j) return false;
  const [lo, hi] = i < j ? [i, j] : [j, i];
  for (let k = lo + cols; k < hi; k += cols) if (!tiles[k]!.cleared) return false;
  return true;
}

export function areConnected(board: Board, i: number, j: number): boolean {
  const { tiles, cols } = board;
  return (
    gridAdjacent(cols, i, j) ||
    readingAdjacent(tiles, i, j) ||
    columnAdjacent(tiles, cols, i, j)
  );
}

/** The single authority on whether a two-tile tap is legal. The server calls this. */
export function canPair(board: Board, cfg: LevelConfig, i: number, j: number): boolean {
  const a = board.tiles[i];
  const b = board.tiles[j];
  if (!a || !b || i === j || a.cleared || b.cleared) return false;
  if (!isMatch(a, b, cfg)) return false;
  return cfg.adjacency ? areConnected(board, i, j) : true;
}

/** The single authority on whether a one-tile tap is right. Hunt rules only. */
export function canPick(board: Board, cfg: LevelConfig, i: number): boolean {
  const t = board.tiles[i];
  if (!t || t.cleared) return false;
  return satisfies(t, cfg);
}

/** Every legal pair on the board right now. Empty for a hunt rule. */
export function findMoves(board: Board, cfg: LevelConfig, limit = Infinity): Array<[number, number]> {
  if (RULES[cfg.rule].kind !== 'pair') return [];
  const live: number[] = [];
  for (let i = 0; i < board.tiles.length; i++) if (!board.tiles[i]!.cleared) live.push(i);
  const out: Array<[number, number]> = [];
  for (let x = 0; x < live.length; x++) {
    for (let y = x + 1; y < live.length; y++) {
      if (canPair(board, cfg, live[x]!, live[y]!)) {
        out.push([live[x]!, live[y]!]);
        if (out.length >= limit) return out;
      }
    }
  }
  return out;
}

/** Uncleared tiles that still satisfy a hunt rule. */
export function findTargets(board: Board, cfg: LevelConfig, limit = Infinity): number[] {
  if (RULES[cfg.rule].kind !== 'hunt') return [];
  const out: number[] = [];
  for (let i = 0; i < board.tiles.length; i++) {
    if (canPick(board, cfg, i)) {
      out.push(i);
      if (out.length >= limit) return out;
    }
  }
  return out;
}

/**
 * What a hint should point at: a legal pair, or one unfound target.
 *
 * One shape for both mechanics, so the button, the counter and the highlight
 * do not each need to know which game they are in.
 */
export function findHint(board: Board, cfg: LevelConfig): number[] | null {
  if (RULES[cfg.rule].kind === 'hunt') {
    const t = findTargets(board, cfg, 1);
    return t.length ? t : null;
  }
  const m = findMoves(board, cfg, 1)[0];
  return m ? [m[0], m[1]] : null;
}

export const hasMove = (board: Board, cfg: LevelConfig): boolean =>
  RULES[cfg.rule].kind === 'hunt'
    ? findTargets(board, cfg, 1).length > 0
    : findMoves(board, cfg, 1).length > 0;

export const remaining = (board: Board): number =>
  board.tiles.reduce((n, t) => n + (t.cleared ? 0 : 1), 0);

/**
 * How many tiles the player still has to find.
 *
 * A hunt board is finished when the last target is gone — the decoys stay on
 * the board on purpose, because "these are the ones that are NOT even" is
 * half of what the exercise teaches.
 */
export const targetsLeft = (board: Board, cfg: LevelConfig): number =>
  RULES[cfg.rule].kind === 'hunt' ? findTargets(board, cfg).length : remaining(board);

/** Has this player finished? The one test both mechanics answer to. */
export const isComplete = (board: Board, cfg: LevelConfig): boolean =>
  targetsLeft(board, cfg) === 0;

/* ── puzzle mode ───────────────────────────────────────────────────────────
 * A picture under the tiles only survives if the dealt grid holds still, so a
 * puzzle board never collapses a row. "Add row" is fine: it appends below the
 * dealt grid, which is the picture's frame, so no piece moves. Both rules are
 * guarded here rather than at each call site, so no caller can forget.
 * ─────────────────────────────────────────────────────────────────────────── */

export const canCollapse = (cfg?: LevelConfig): boolean => !cfg?.puzzle;
export const canRefill = (_cfg?: LevelConfig): boolean => true;

/* ─── mutation ──────────────────────────────────────────────────────────── */

/** Clears a validated pair. Caller must have checked `canPair`. */
export function clearPair(board: Board, i: number, j: number): void {
  board.tiles[i]!.cleared = true;
  board.tiles[j]!.cleared = true;
}

/** Clears one validated tile. Caller must have checked `canPick`. */
export function clearOne(board: Board, i: number): void {
  board.tiles[i]!.cleared = true;
}

/**
 * Removes rows in which every tile is cleared and shifts the rest up, exactly
 * as the board in the video collapses. Returns how many rows went.
 */
export function collapseRows(board: Board, cfg?: LevelConfig): number {
  if (!canCollapse(cfg)) return 0;
  const kept: Tile[] = [];
  let removed = 0;
  for (let r = 0; r < board.rows; r++) {
    const row = board.tiles.slice(r * board.cols, (r + 1) * board.cols);
    if (row.length && row.every((t) => t.cleared)) removed++;
    else kept.push(...row);
  }
  if (removed > 0) {
    board.tiles = kept;
    board.rows = Math.ceil(kept.length / board.cols);
  }
  return removed;
}

/**
 * "Add row": appends every surviving tile, in reading order, to the bottom.
 * This is the mechanic that keeps a board eventually winnable — without it a
 * board with no legal move left is simply lost.
 */
export function refill(board: Board, cfg?: LevelConfig): number {
  if (!canRefill(cfg)) return 0;
  const live = board.tiles.filter((t) => !t.cleared);
  if (live.length === 0) return 0;
  let nextId = board.tiles.reduce((m, t) => Math.max(m, t.id), 0) + 1;
  for (const t of live) {
    board.tiles.push({ id: nextId++, label: t.label, value: t.value, cleared: false });
  }
  board.rows = Math.ceil(board.tiles.length / board.cols);
  return live.length;
}

/* ─── generation ────────────────────────────────────────────────────────── */

interface Cell { label: string; value: number; cleared?: boolean }

function layout(faces: Cell[], cols: number): Board {
  const tiles: Tile[] = faces.map((f, i) => ({
    id: i, label: f.label, value: f.value, cleared: f.cleared ?? false,
  }));
  return { tiles, cols, rows: Math.ceil(tiles.length / cols) };
}

/**
 * How much of this board can actually be cleared?
 *
 * Exhaustive search is exponential, so this runs a randomised greedy pass
 * several times and keeps the best result — cheap, and more than accurate
 * enough to reject a board that would strand a class halfway through a round.
 */
export function clearableFraction(board: Board, cfg: LevelConfig, rng: Rng, tries = 6): number {
  const total = board.tiles.filter((t) => !t.cleared).length;
  if (total === 0) return 1;
  let best = 0;

  for (let t = 0; t < tries; t++) {
    const work: Board = {
      cols: board.cols,
      rows: board.rows,
      tiles: board.tiles.map((x) => ({ ...x })),
    };
    let cleared = 0;
    for (;;) {
      const moves = findMoves(work, cfg, 40);
      if (moves.length === 0) break;
      const [i, j] = rng.pick(moves);
      clearPair(work, i, j);
      cleared += 2;
    }
    best = Math.max(best, cleared / total);
    if (best === 1) break;
  }
  return best;
}

/** Pair rules where one tile can have several possible partners. */
const CROSS_MATCHING: ReadonlySet<string> = new Set(['multipleOf', 'sameProduct']);

/* ─── rescue ────────────────────────────────────────────────────────────────
 * Even a well-dealt board can be stranded by the order a child plays it in.
 * When that happens and "Add row" cannot help, the tiles still standing are
 * re-dealt in place: same positions, fresh pairs. The child keeps their score
 * and their progress, and the board can always be finished.
 * ─────────────────────────────────────────────────────────────────────────── */

/** A pair board with tiles left and no legal move. */
export const isStuck = (board: Board, cfg: LevelConfig): boolean =>
  RULES[cfg.rule].kind === 'pair' && remaining(board) > 0 && !hasMove(board, cfg);

/**
 * Should a stuck board be re-dealt now?
 *
 * Only when "Add row" cannot get the child moving again: none left, a puzzle
 * board (which has none), or a refill that would still leave no move — on a
 * multiples board, copying 9 · 9 gives four 9s and still no pair.
 */
export function needsRescue(board: Board, cfg: LevelConfig, addsLeft: number): boolean {
  if (!isStuck(board, cfg)) return false;
  if (addsLeft <= 0 || !canRefill(cfg)) return true;
  const trial = cloneBoard(board);
  refill(trial, cfg);
  return !hasMove(trial, cfg);
}

/**
 * Re-deals every uncleared tile of a stuck board in place. Returns whether
 * anything changed. Tile ids are kept, so the board does not re-mount.
 */
export function rescueBoard(board: Board, cfg: LevelConfig, seed: number): boolean {
  const rule = RULES[cfg.rule];
  if (rule.kind !== 'pair' || !isStuck(board, cfg)) return false;
  const live: number[] = [];
  board.tiles.forEach((t, i) => { if (!t.cleared) live.push(i); });
  const rng = new Rng(seed);

  const candidate = (): Board => {
    const work = cloneBoard(board);
    const faces: Face[] = [];
    const ctx = newDealCtx();
    for (let p = 0; p < Math.floor(live.length / 2); p++) {
      const [a, b] = (rule as PairRule).pair(rng, cfg, ctx);
      faces.push(a, b);
    }
    rng.shuffle(faces);
    live.forEach((idx, k) => {
      const f = faces[k];
      const tile = work.tiles[idx]!;
      // An odd tile out has nobody to pair with: it goes, rather than
      // becoming the next stranded tile.
      if (f) { tile.label = f.label; tile.value = f.value; } else tile.cleared = true;
    });
    return work;
  };

  let best = candidate();
  let bestScore = clearableFraction(best, cfg, rng);
  for (let i = 0; i < 12 && bestScore < 1; i++) {
    const cand = candidate();
    const score = clearableFraction(cand, cfg, rng);
    if (score > bestScore) { best = cand; bestScore = score; }
  }
  board.tiles = best.tiles;
  return true;
}

/** Every value in the level's window, capped so a huge range stays cheap. */
const RANGE_CAP = 4000;

function splitRange(cfg: LevelConfig): { targets: number[]; decoys: number[] } {
  const rule = RULES[cfg.rule];
  const [lo, hi] = valueRange(cfg);
  const targets: number[] = [];
  const decoys: number[] = [];
  const top = Math.min(hi, lo + RANGE_CAP);
  for (let v = lo; v <= top; v++) {
    if (rule.kind !== 'hunt') continue;
    if (rule.exclude?.(v, cfg)) continue;
    (rule.test(v, cfg) ? targets : decoys).push(v);
  }
  return { targets, decoys };
}

/**
 * A hunt board: a controlled mix of numbers that answer the question and
 * numbers that do not.
 *
 * Density matters more than it looks. At 80% targets there is nothing to
 * reject and the child taps everything; at 15% they hunt an empty field. The
 * default sits just under half, and it is a teacher setting either way.
 */
function buildHunt(rng: Rng, cfg: LevelConfig, cells: number): Cell[] {
  const { targets, decoys } = splitRange(cfg);
  const out: Cell[] = [];

  if (targets.length === 0) {
    // Nothing in this window answers the question. Rather than deal a board
    // that cannot be finished, fall back to a board of decoys plus the
    // smallest legal target the rule can produce.
    for (let i = 0; i < cells; i++) {
      const v = decoys.length ? rng.pick(decoys) : i + 1;
      out.push({ label: String(v), value: v });
    }
    return rng.shuffle(out);
  }

  const density = Math.min(0.8, Math.max(0.15, cfg.huntDensity || 0.45));
  let wanted = Math.round(cells * density);
  wanted = Math.max(1, Math.min(cells, wanted));
  const decoyCount = decoys.length === 0 ? 0 : cells - wanted;
  if (decoys.length === 0) wanted = cells;

  // Every distinct value gets a turn before any repeats, so a board of eight
  // targets is not four copies of "4".
  const draw = (pool: readonly number[], n: number): void => {
    const bag: number[] = [];
    while (bag.length < n) bag.push(...rng.shuffle([...pool]));
    for (let i = 0; i < n; i++) {
      const v = bag[i]!;
      out.push({ label: String(v), value: v });
    }
  };

  draw(targets, wanted);
  if (decoyCount > 0) draw(decoys, decoyCount);
  return rng.shuffle(out);
}

/**
 * Builds a board for a level.
 *
 * Without adjacency (G1–2) a board made of whole pairs is always fully
 * clearable, so it is emitted directly. With adjacency the layout decides
 * solvability, so candidates are generated and scored, and the best is kept.
 */
export function generateBoard(cfg: LevelConfig, seed: number, recent?: ReadonlySet<number>): Board {
  return dealBoard(cfg, seed, recent).board;
}

/**
 * The same deal, plus the bases it consumed.
 *
 * Callers that deal board after board — the solo game and the session manager —
 * pass those bases back in as the next deal's `recent`, which is what stops the
 * same numbers leading two boards in a row.
 */
export function dealBoard(
  cfg: LevelConfig, seed: number, recent?: ReadonlySet<number>,
): { board: Board; bases: Set<number> } {
  const rng = new Rng(seed);
  const cells = Math.max(2, cfg.rows * cfg.cols);
  const rule = RULES[cfg.rule];

  if (rule.kind === 'hunt') {
    return { board: layout(buildHunt(rng, cfg, cells), cfg.cols), bases: new Set() };
  }

  const pairs = Math.floor(cells / 2);
  const odd = cells - pairs * 2;

  const build = (r: Rng): { board: Board; bases: Set<number> } => {
    const ctx = newDealCtx(recent);
    const faces: Cell[] = [];
    for (let p = 0; p < pairs; p++) {
      const [a, b] = rule.pair(r, cfg, ctx);
      faces.push(a as Face, b as Face);
    }
    if (odd) {
      // An odd cell count leaves one tile with no partner. It used to be dealt
      // live and left to wait for "Add row" — which is exactly the stranded
      // tile teachers reported, on any level with no adds left. So the spare
      // cell is always dealt already open; on a puzzle board its piece of the
      // picture simply shows from the start.
      const spare = faces[r.int(0, faces.length - 1)]!;
      faces.push({ ...spare, cleared: true });
    }
    return { board: layout(r.shuffle(faces), cfg.cols), bases: new Set(ctx.used.keys()) };
  };

  // Without adjacency a board of whole pairs is clearable by construction —
  // but where a tile can pair with more than one partner, the order the child
  // picks can still strand tiles (3 · 9 · 9 · 27: take 3 ↔ 27 and the 9s are
  // left). Those rules are scored too, so the deal that strands least wins.
  const scored = cfg.adjacency || CROSS_MATCHING.has(cfg.rule);
  if (!scored || !cfg.guaranteeSolvable) return build(rng);

  const attempts = 12;
  let best = build(rng);
  let bestScore = clearableFraction(best.board, cfg, rng);
  for (let attempt = 0; attempt < attempts && bestScore < 1; attempt++) {
    const cand = build(rng);
    const score = clearableFraction(cand.board, cfg, rng);
    if (score > bestScore) {
      best = cand;
      bestScore = score;
    }
  }
  return best;
}

/** Deep copy — used by the server to fork a template board per player. */
export const cloneBoard = (b: Board): Board => ({
  cols: b.cols,
  rows: b.rows,
  tiles: b.tiles.map((t) => ({ ...t })),
});
